import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchBuildings,
  fetchBuildingsOfCompany,
} from "../../../../api/building";
import { attachDeviceToBuilding } from "../../../../api/device";
import {
  Building,
  DeviceWithoutLegacyPassword,
} from "../../../../generated/types";
import { useAccountStorage } from "../../../../hooks/useAccountStorage";
import { I18nKey } from "../../../../i18n/i18n";
import { getDeviceDisplayName } from "../../../../utils/breedingDevices";
import { Button } from "../../../common/Button";
import { FormSelect } from "../../../common/FormLayout";
import { Modal, ModalActions } from "../../../common/Modal";
import { sectionToIcon } from "../../../type/default";
import { DeviceTypeBadge } from "../../device/DeviceTypeBadge";

/**
 * Rattache à un bâtiment un produit de l'entreprise qui n'en a pas. Les bâtiments viennent
 * de la même source que l'association d'un produit : ceux de l'éleveur, ou ceux du client
 * pour un installateur.
 */
export const AttachDeviceModal = ({
  device,
  companyId,
  breedingNames,
  onClose,
  onAttached,
}: {
  device: DeviceWithoutLegacyPassword;
  companyId: number;
  /** Nom des élevages connus, pour distinguer deux « Bâtiment 1 » d'élevages différents. */
  breedingNames: Map<number, string>;
  onClose: () => void;
  onAttached: () => void;
}) => {
  const { t } = useTranslation();
  const { getAccountStorage } = useAccountStorage();
  const isBreeder = getAccountStorage()?.type === "breeder";
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [idBuilding, setIdBuilding] = useState("");
  const [error, setError] = useState<I18nKey | "">("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = isBreeder
      ? fetchBuildings().then((result) => ({
          buildings: result.Buildings,
          error: result.error,
        }))
      : fetchBuildingsOfCompany(companyId);
    load.then((result) => {
      if (cancelled) return;
      setBuildings(result.buildings);
      // i18n.t("device-attach-building-load-error")
      setError(result.error ? "device-attach-building-load-error" : "");
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isBreeder, companyId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!idBuilding) return;
    setSubmitting(true);
    const result = await attachDeviceToBuilding(device.id, Number(idBuilding));
    setSubmitting(false);
    if (result) {
      setError(result);
      return;
    }
    onAttached();
  };

  const buildingLabel = (building: Building) => {
    const breedingName = breedingNames.get(building.id_breeding);
    return breedingName ? `${breedingName} — ${building.name}` : building.name;
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t("device-attach-building")}
      icon={sectionToIcon.section_building}
      maxWidth="md"
    >
      <form onSubmit={submit} className="space-y-4 py-2">
        <p className="flex items-center gap-2 text-default-text">
          <DeviceTypeBadge type={device.type} />
          <span className="truncate">{getDeviceDisplayName(device)}</span>
        </p>
        {isLoading ? (
          <p className="text-sm text-grey-readable">{t("loading")}</p>
        ) : buildings.length === 0 && !error ? (
          <p className="text-sm text-grey-readable">
            {t("device-attach-building-none")}
          </p>
        ) : (
          buildings.length > 0 && (
            <FormSelect
              label={t("device-attach-building-label")}
              name="id_building"
              required
              value={idBuilding}
              onChange={(e) => {
                setIdBuilding(e.target.value);
                setError("");
              }}
              options={buildings.map((building) => ({
                value: String(building.id),
                label: buildingLabel(building),
              }))}
            />
          )
        )}
        {error && <p className="text-sm text-red-600">{t(error)}</p>}
        <ModalActions>
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("button-cancel")}
          </Button>
          <Button type="submit" disabled={!idBuilding || submitting}>
            {submitting
              ? t("setup-saving")
              : t("device-attach-building-submit")}
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
};
